import { Children, useRef, useState } from "react";
import SVG from "../../SVG";

export default function MobileCarousel({
  children,
  mobileFullWidth = false,
  compactGap = false,
  alignStart = false,
  desktopOverflowVisible = false,
  itemLabel = "کارت",
  className = "",
}) {
  const slides = Children.toArray(children);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const slideRefs = useRef([]);

  const goToSlide = (index) => {
    const nextIndex = Math.max(0, Math.min(slides.length - 1, index));
    slideRefs.current[nextIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
    setCurrentIndex(nextIndex);
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const containerBounds = container.getBoundingClientRect();
    const containerCenter = containerBounds.left + containerBounds.width / 2;
    let closestIndex = 0;
    let closestDistance = Infinity;

    slideRefs.current.forEach((slide, index) => {
      if (!slide) return;
      const slideBounds = slide.getBoundingClientRect();
      const slideCenter = slideBounds.left + slideBounds.width / 2;
      const distance = Math.abs(containerCenter - slideCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    setCurrentIndex((previousIndex) =>
      previousIndex === closestIndex ? previousIndex : closestIndex,
    );
  };

  return (
    <div className="relative w-full pb-7 mb:pb-0">
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={`flex w-full justify-start overflow-x-auto scroll-smooth snap-x snap-mandatory mb:justify-evenly mb:scroll-auto mb:snap-none ${
          alignStart ? "items-start" : "items-center"
        } ${compactGap ? "gap-0 mb:gap-5" : "gap-10"} ${
          desktopOverflowVisible ? "lg:overflow-visible" : ""
        } ${className}`}
      >
        {slides.map((slide, index) => (
          <div
            key={slide.key ?? index}
            ref={(element) => {
              slideRefs.current[index] = element;
            }}
            className={
              mobileFullWidth
                ? "w-full shrink-0 snap-center mb:contents"
                : "shrink-0 snap-center"
            }
          >
            {slide}
          </div>
        ))}
      </div>

      {currentIndex < slides.length - 1 && (
        <button
          type="button"
          aria-label={`${itemLabel} بعدی`}
          onClick={() => goToSlide(currentIndex + 1)}
          className="absolute -left-3 top-1/2 z-30 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/40 opacity-60 shadow-md backdrop-blur-md transition-opacity hocus:opacity-100 dark:bg-black/30 mb:hidden"
        >
          <SVG
            name="arrowLeft"
            classname="size-3 stroke-zi-blue-dark dark:stroke-white"
          />
        </button>
      )}

      {currentIndex > 0 && (
        <button
          type="button"
          aria-label={`${itemLabel} قبلی`}
          onClick={() => goToSlide(currentIndex - 1)}
          className="absolute -right-3 top-1/2 z-30 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/40 opacity-60 shadow-md backdrop-blur-md transition-opacity hocus:opacity-100 dark:bg-black/30 mb:hidden"
        >
          <SVG
            name="arrowLeft"
            classname="size-3 rotate-180 stroke-zi-blue-dark dark:stroke-white"
          />
        </button>
      )}

      <div className="absolute bottom-0 right-1/2 z-30 flex translate-x-1/2 gap-2 rounded-full bg-black/10 p-1 backdrop-blur-2xl dark:bg-white/15 mb:hidden">
        {slides.map((slide, index) => (
          <button
            key={slide.key ?? index}
            type="button"
            aria-label={`نمایش ${itemLabel} ${index + 1}`}
            onClick={() => goToSlide(index)}
            className={`size-1.5 rounded-full transition-colors ${
              currentIndex === index
                ? "bg-zi-blue-light dark:bg-white"
                : "bg-zi-blue-light/40 dark:bg-white/50"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
